<script setup lang="ts">
import type { TTutorState } from '@/store/tutor/state'
import { computed } from 'vue'

const props = defineProps<{
    chat: TTutorState['chat']
    isExpanded: boolean
}>()

const visibleMessages = computed(() => {
    const chat = [ ...props.chat ].map(c => ({
        ...c,
        content: formatTutorMessageContent(c.content),
    }))

    if (props.isExpanded) {
        return chat
    }

    // if the first message is a priming message, remove it when minimized
    if (chat[0].role === 'assistant') {
        chat.shift()
    }

    return chat.slice(0, 2)
})

// Escape HTML to prevent XSS
// then convert **words** to bold and *words* to italic
// and add paragraph breaks
const formatTutorMessageContent = (raw: string): string => {
    if (!raw) {
        return ''
    }
    const escaped = raw
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>')
}
</script>

<template>
    <div class="notebook-tutor-session">
        <div
            v-for="message in visibleMessages"
            :key="message.content"
            class="notebook-tutor-session__message"
            :class="{ [`notebook-tutor-session__message--${message.role}`]: true }"
        >
            <div
                class="notebook-tutor-session__message-inner"
                :class="{ [`notebook-tutor-session__message-inner--${message.role}`]: true }"
                v-dark
                v-html="message.content"
            />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.notebook-tutor-session {
    display: flex;
    flex-direction: column;

    &__message {
        margin-bottom: 10px;

        &:last-child {
            margin-bottom: 0;
        }

        &--user {
            margin-left: 44px;
        }

        &--assistant {
            max-width: calc(100% - 44px);
            width: 100%;
        }
    }

    &__message-inner {
        font-size: 15px;
        line-height: 20px;

        &--user {
            float: right;
            background-color: $buttermilk;
            padding: 8px 12px;
            font-size: 15px;
            line-height: 20px;
            border-radius: 10px;

            &--dark {
                background: rgba($buttermilk, 0.2);
            }
        }
    }
}
</style>
